/**
 * D7 · রসিদ — `/receipt/:ref`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame D7 (360 × 680).
 *
 * Frame note: "The dashed rule and the itemised block make it read as a paper receipt,
 * which is what it will be forwarded as — often to the family member who paid."
 *
 * The two header icons repeat the two footer buttons, so their accessible names are the
 * footer's own drawn words (`ডাউনলোড`, `শেয়ার করুন`) rather than invented strings.
 *
 * ── WIRED ────────────────────────────────────────────────────────────────────
 * `GET /v1/orders/:id`, where the id is the reference the URL carries — the same short
 * string a person reads aloud to support. The rows are the server's, including the date,
 * which is composed from CLDR's Bangla month names rather than from a list anybody wrote
 * (`commerce/copy.ts § placedAtBn`), and the amounts arrive already formatted.
 *
 * DEVIATION: the partner disclosure is added. `Oprappo Architecture.dc.html` § fulfilment
 * says of partner mode: "পার্টনারের নাম রসিদে প্রকাশিত" — the receipt is precisely where
 * the partner's name is promised, and this frame draws none.
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Icon } from '@oprappo/ui';
import type { OrderResponse } from '@oprappo/contracts';
import { assertPartnerDisclosure } from '@oprappo/contracts';
import { Frame, Mark, PartnerDisclosure, SAND_FRAME, ScreenHeader } from '../offering/chrome.js';
import { Loaded, commerce, useResource } from '../offering/commerce.js';

/** The share glyph D7 draws: three nodes and two links, `2px`, teal. */
function ShareGlyph(): JSX.Element {
  return (
    <svg width={19} height={19} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="18" cy="6" r="2.6" stroke="#0E3A45" strokeWidth={2} />
      <circle cx="6" cy="12" r="2.6" stroke="#0E3A45" strokeWidth={2} />
      <circle cx="18" cy="18" r="2.6" stroke="#0E3A45" strokeWidth={2} />
      <path d="M8.4 10.7l7.2-3.4M8.4 13.3l7.2 3.4" stroke="#0E3A45" strokeWidth={2} />
    </svg>
  );
}

export function D7ReceiptView({ order }: { order: OrderResponse }): JSX.Element {
  const navigate = useNavigate();
  // A receipt is the money screen that outlives every other one: it is forwarded, saved
  // and shown to whoever paid. The partner must be named on it (docs/05 § fulfilment).
  assertPartnerDisclosure(order);
  const receipt = order.receipt;
  const rows = receipt.rows;

  return (
    <Frame className={SAND_FRAME}>
      <ScreenHeader
        titleBn={receipt.title_bn}
        onBack={() => navigate(-1)}
        trailing={
          <div className="flex gap-[14px] flex-none">
            <button
              type="button"
              aria-label={receipt.download_label_bn}
              className="bg-transparent border-0 p-0 rounded-none inline-flex"
            >
              {/* The frame's download arrow is the same path § 02 draws for upload. */}
              <Icon name="upload" size={19} />
            </button>
            <button
              type="button"
              aria-label={receipt.share_label_bn}
              className="bg-transparent border-0 p-0 rounded-none inline-flex"
            >
              <ShareGlyph />
            </button>
          </div>
        }
      />

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div className="bg-white border border-border p-[16px]">
          <div className="text-center pb-[15px] border-b border-dashed border-border-warm">
            {/* Drawn inline in a `text-align:center` block, so the line box below it is part
                of the spacing. `flex` would delete that. */}
            <Mark size={30} className="inline mb-[10px]" />
            {/*
              * ── THE BADGE IS DRAWN ONLY WHEN IT IS TRUE ──────────────────────
              * D7's frame prints `পরিশোধিত` — paid — unconditionally, and this screen
              * printed it on every receipt including a cash-on-delivery order where
              * nothing had been paid.
              *
              * The server now sends the drawn word only for a CONFIRMED payment and an
              * empty string otherwise (`contracts § receiptPaidLabel`), and this element
              * is omitted rather than rendered empty — an empty text node would leave the
              * line box and the `mt-[5px]` gap behind it, which is a blank space where a
              * claim used to be rather than the absence of one.
              *
              * The word for "not paid yet" is NOT written here. It is the first row of
              * `docs/bangla-needed/payments.md`: a badge on the most forwarded screen in
              * the product is not a string a machine may choose.
              *
              * A confirmed receipt — which is what the fixture and the frozen gate score —
              * renders exactly as it did before.
              */}
            {receipt.paid_label_bn === '' ? null : (
              <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted">
                {receipt.paid_label_bn}
              </div>
            )}
            <div className="font-manrope font-bold text-[26px] leading-[1.15] text-ink tabular-nums mt-[5px]">
              {order.total.display}
            </div>
          </div>
          <div className="pt-[5px]">
            {rows.map((r, i) => (
              <div
                key={r.label_bn}
                className={
                  'py-[11px] flex justify-between items-baseline gap-[11px]' +
                  (i < rows.length - 1 ? ' border-b border-border' : '')
                }
              >
                <div className="font-anek font-normal text-[14px] leading-[1.75] text-muted">
                  {r.label_bn}
                </div>
                <div
                  className={
                    r.script === 'latin'
                      ? 'font-manrope font-semibold text-[14.5px] leading-[1.35] text-ink'
                      : r.script === 'money'
                        ? 'font-manrope font-medium text-[14.5px] leading-[1.35] text-ink tabular-nums'
                        : 'font-anek font-medium text-[14.5px] leading-[1.75] text-ink'
                  }
                >
                  {r.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="font-anek font-normal text-[13px] leading-[1.75] text-body mt-[14px]">
          {receipt.note_bn}
        </div>

        {/* Added — the architecture promises the partner's name on the receipt. */}
        <PartnerDisclosure partnerName={order.partner_name} className="mt-[14px]" />
      </div>

      <div className="bg-white border-t border-border px-[16px] py-[12px] flex gap-[10px]">
        <Button tone="secondary" size="md" className="flex-1 box-content p-0">
          {receipt.download_label_bn}
        </Button>
        <Button tone="secondary" size="md" className="flex-1 box-content p-0">
          {receipt.share_label_bn}
        </Button>
      </div>
    </Frame>
  );
}

export function D7Receipt(): JSX.Element {
  const { ref } = useParams();
  const reference = ref ?? '';
  const load = useCallback(
    (signal: AbortSignal) => commerce.order(reference, signal),
    [reference],
  );
  const [resource] = useResource(load, `receipt:${reference}`);

  return <Loaded resource={resource}>{(order) => <D7ReceiptView order={order} />}</Loaded>;
}
